import { AlertTriangle, Info } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { triggerPreviewGeneration } from "@/api/previews.api";
import { listActiveCategories } from "@/api/categories.api";
import { useJobPolling } from "@/hooks/useJobPolling";
import { useMe } from "@/hooks/useMe";
import { useThemesList } from "@/hooks/useThemesList";
import { BatchActionsBar } from "@/pages/catalog/components/BatchActionsBar";
import { JobProgressPanel } from "@/pages/catalog/components/JobProgressPanel";
import type { CategorySummary } from "@/types/catalog";
import type { GeneratePreviewsRequest } from "@/types/previews";

import { ThemeCard } from "./components/ThemeCard";

import "./ThemesListPage.css";

const SKELETON_COUNT = 6;

/**
 * CR-111 — `/themes` top-level admin page.
 *
 * Combines the CR-100/110 batch-generate flow (BatchActionsBar +
 * GeneratePreviewDialog + JobProgressPanel) with the new themes grid
 * driven by `useThemesList`. Each row surfaces a ThemeCard with a
 * direct link to `/themes/:slug`.
 *
 * Role-gating follows the ThemePreviewsPage pattern: SUPER_ADMIN sees
 * the BatchActionsBar; all other roles see a read-only info banner.
 */
export function ThemesListPage(): JSX.Element {
  const { me } = useMe();
  const {
    themes,
    loading,
    error,
    page,
    totalPages,
    hasNextPage,
    setPage,
    refetch,
  } = useThemesList();

  const [categories, setCategories] = useState<readonly CategorySummary[]>([]);
  const [activeJob, setActiveJob] = useState<string | null>(null);
  const polling = useJobPolling(activeJob);

  // Categories power the chip on each ThemeCard. Loaded once on mount.
  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();
    listActiveCategories(ctrl.signal)
      .then((resp) => {
        if (cancelled) return;
        setCategories(resp.items);
      })
      .catch(() => {
        // Category name lookup is a nice-to-have; swallowing keeps the
        // grid usable even if the categories endpoint hiccups.
      });
    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, []);

  // Refresh the themes grid whenever polling ends cleanly — is_stale /
  // last_generated may have changed.
  useEffect(() => {
    if (polling.stopped && !polling.error && activeJob !== null) {
      refetch();
    }
  }, [polling.stopped, polling.error, activeJob, refetch]);

  const categoriesById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of categories) map.set(c.id, c.name);
    return map;
  }, [categories]);

  const canTrigger = me?.system_role === "SUPER_ADMIN";
  const jobInFlight = activeJob !== null && !polling.stopped;

  const handleTrigger = useCallback(
    async (req: GeneratePreviewsRequest): Promise<void> => {
      try {
        const resp = await triggerPreviewGeneration(req);
        setActiveJob(resp.job_id);
      } catch {
        // fetchApi already surfaces errors via API_ERROR_BUS.
      }
    },
    [],
  );

  const handlePrev = useCallback(() => {
    if (page > 1) setPage(page - 1);
  }, [page, setPage]);

  const handleNext = useCallback(() => {
    if (hasNextPage) setPage(page + 1);
  }, [page, hasNextPage, setPage]);

  return (
    <section className="themes-list-page">
      <header className="themes-list-page__header">
        <h1>Themes</h1>
        <p className="themes-list-page__lede">
          Global theme catalog. Open a theme to see its generated previews, or
          trigger a batch regeneration.
        </p>
      </header>

      {!canTrigger ? (
        <div className="error-banner error-banner--info" role="note">
          <span className="error-banner__icon" aria-hidden>
            <Info size={18} />
          </span>
          <div className="error-banner__content">
            <div className="error-banner__title">Read-only view</div>
            <div className="error-banner__message">
              You need SUPER_ADMIN to regenerate previews. Contact an operator.
            </div>
          </div>
        </div>
      ) : (
        <BatchActionsBar
          disabled={jobInFlight}
          onTrigger={(req) => {
            void handleTrigger(req);
          }}
        />
      )}

      {loading ? (
        <div className="themes-list-page__grid" aria-busy="true">
          {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
            <div
              key={i}
              className="themes-list-page__skeleton-card skeleton-fade-in"
              aria-hidden="true"
            >
              <div className="themes-list-page__skeleton-hero skeleton" />
              <div className="themes-list-page__skeleton-body">
                <div className="skeleton skeleton--title" />
                <div className="skeleton skeleton--text skeleton--text-short" />
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="error-banner" role="alert">
          <span className="error-banner__icon" aria-hidden>
            <AlertTriangle size={18} />
          </span>
          <div className="error-banner__content">
            <div className="error-banner__title">Failed to load themes</div>
            <div className="error-banner__message">{error.message}</div>
          </div>
        </div>
      ) : themes.length === 0 ? (
        <div className="themes-list-page__empty">
          <div className="themes-list-page__empty-card">
            <h2>No themes yet</h2>
            <p>
              The theme catalog is empty. Seed themes via the backend or
              ask an operator.
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="themes-list-page__grid">
            {themes.map((theme) => (
              <ThemeCard
                key={theme.id}
                theme={theme}
                categoryName={categoriesById.get(theme.category_id)}
              />
            ))}
          </div>
          {totalPages > 1 ? (
            <nav
              className="themes-list-page__pagination"
              aria-label="Themes pagination"
            >
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={handlePrev}
                disabled={page <= 1}
              >
                Previous
              </button>
              <span className="themes-list-page__page-info">
                Page {page} of {totalPages}
              </span>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={handleNext}
                disabled={!hasNextPage}
              >
                Next
              </button>
            </nav>
          ) : null}
        </>
      )}

      <JobProgressPanel
        status={polling.status}
        stopped={polling.stopped}
        error={polling.error}
      />
    </section>
  );
}
